import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { addDoc, updateDoc, doc, collection, serverTimestamp } from 'firebase/firestore';

export default function EventDialog({ open, onClose, event, channelId, firestoreInstance, authUser }) {
  const isEdit = Boolean(event);
  const [title, setTitle] = useState(event?.title || '');
  const [description, setDescription] = useState(event?.description || '');
  const [start, setStart] = useState(event?.start ? new Date(event.start.seconds ? event.start.seconds * 1000 : event.start) : new Date());
  const [end, setEnd] = useState(
    event?.end
      ? new Date(event.end.seconds ? event.end.seconds * 1000 : event.end)
      : (() => {
          const s = event?.start ? new Date(event.start.seconds ? event.start.seconds * 1000 : event.start) : new Date();
          const e = new Date(s);
          e.setHours(e.getHours() + 1);
          return e;
        })()
  );
  const [error, setError] = useState('');

  useEffect(() => {
    setTitle(event?.title || '');
    setDescription(event?.description || '');
    setStart(event?.start ? new Date(event.start.seconds ? event.start.seconds * 1000 : event.start) : new Date());
    setEnd(
      event?.end
        ? new Date(event.end.seconds ? event.end.seconds * 1000 : event.end)
        : (() => {
            const s = event?.start ? new Date(event.start.seconds ? event.start.seconds * 1000 : event.start) : new Date();
            const e = new Date(s);
            e.setHours(e.getHours() + 1);
            return e;
          })()
    );
    setError('');
  }, [event, open]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!start || isNaN(start.getTime())) {
      setError('Start time is required.');
      return;
    }
    if (!end || isNaN(end.getTime())) {
      setError('End time is required.');
      return;
    }
    if (end < start) {
      setError('End time must be after start time.');
      return;
    }
    const data = {
      title,
      description,
      start,
      end,
      updatedAt: serverTimestamp(),
      channelId, // Add channelId for better structure
    };
    if (isEdit) {
      await updateDoc(doc(firestoreInstance, 'channels', channelId, 'events', event.id), data);
    } else {
      const docRef = await addDoc(
        collection(firestoreInstance, 'channels', channelId, 'events'),
        {
          ...data,
          createdBy: authUser?.user?.uid,
          createdAt: serverTimestamp(),
        }
      );
      // Add the generated id to the event document
      await updateDoc(docRef, { id: docRef.id });
    }
    onClose();
  };

  // Validation logic for Save button
  const isSaveDisabled =
    !title.trim() ||
    !start || isNaN(start.getTime()) ||
    !end || isNaN(end.getTime()) ||
    end < start;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Event' : 'Add Event'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Title" value={title} onChange={e => setTitle(e.target.value)} fullWidth required />
          <TextField label="Description" value={description} onChange={e => setDescription(e.target.value)} fullWidth multiline minRows={2} />
          <DateTimePicker
            label="Start Time"
            value={start}
            onChange={setStart}
            slotProps={{ textField: { fullWidth: true, required: true } }}
          />
          <DateTimePicker
            label="End Time"
            value={end}
            onChange={setEnd}
            slotProps={{ textField: { fullWidth: true } }}
          />
          {error && <span style={{ color: 'red' }}>{error}</span>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaveDisabled}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
